import { ReactNode, useMemo } from 'react';
import { MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { Trans, useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { ConfirmationModal, InlineNotification, Modal, Text } from '@frontend/design-system';
import {
  findEventById,
  type DragDropState,
  type DropPayload,
} from '../../../hooks/scheduler-v3/use-appointment-drag-drop';
import { useCalendarLocationIds } from '../../../stores/calendar-location.store';
import { getTimeDiff } from '../../../utils/date-helpers';
import { useCalendarViewProps } from '../providers';
import { CalendarViewOffices } from '../types';
import { checkIsNonAttendedEventStatus } from '../utils';

const findEntityName = (data: CalendarViewOffices, entityId: string) => {
  const entity = findTargetColumnEntity(data, entityId);
  if (!entity) return '';
  return entity.entityName || getFullName(entity);
};

const minutesToTimeString = (minutesSinceCalendarStart: number, calendarStartHour: number): string => {
  const totalMinutes = minutesSinceCalendarStart + calendarStartHour * 60;
  return dayjs().startOf('day').add(totalMinutes, 'minute').format('hh:mm A');
};

const formatDateTime = (calendarDate: string, time: string) => {
  return `${formatDate(calendarDate, 'MMM D, YYYY') || calendarDate}, ${time}`;
};

const findTargetColumnEntity = (data: CalendarViewOffices, targetColumnEntityId: string) => {
  if (!data) return undefined;
  for (const office of data) {
    const entity = office.columnEntities?.find((e) => e.entityId === targetColumnEntityId);
    if (entity) return entity;
  }
  return undefined;
};

const parseTimeToMinutes = (timeStr: string): number => {
  const time = dayjs(timeStr, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm']);
  return time.isValid() ? time.diff(time.startOf('day'), 'minute') : 0;
};

const checkForOverlappingAppointments = (
  data: CalendarViewOffices,
  pendingDropPayload: DropPayload,
  calendarStartHour: number
): boolean => {
  const { eventId, targetColumnEntityId, newStartTimeMinutes } = pendingDropPayload;

  const draggedEvent = findEventById(data, eventId);
  if (!draggedEvent) return false;

  const durationMinutes = getTimeDiff(draggedEvent.startHour, draggedEvent.endHour, { format: 'hh:mm A' });

  // Calculate the new time range (in minutes from midnight)
  const newStartMinutesFromMidnight = newStartTimeMinutes + calendarStartHour * 60;
  const newEndMinutesFromMidnight = newStartMinutesFromMidnight + durationMinutes;

  const targetEntity = findTargetColumnEntity(data, targetColumnEntityId);

  if (!targetEntity?.events) {
    return false;
  }

  // Check for overlaps with other events
  return targetEntity.events.some((event) => {
    const isNonAttendedEvent = checkIsNonAttendedEventStatus(event.status);
    if (event.type === 'unavailable' || isNonAttendedEvent || event.eventId === eventId) return false;
    const eventStartMinutes = parseTimeToMinutes(event.startHour);
    const eventEndMinutes = parseTimeToMinutes(event.endHour);
    const overlaps = newStartMinutesFromMidnight < eventEndMinutes && eventStartMinutes < newEndMinutesFromMidnight;
    return overlaps;
  });
};

const checkAppointmentTypeMapping = (
  data: CalendarViewOffices,
  pendingDropPayload: DropPayload,
  providerAppointmentTypeMappings: {
    appointmentTypeId?: string;
    providerId?: string;
  }[]
): boolean => {
  const { eventId, targetColumnEntityId, sourceColumnEntityId } = pendingDropPayload;

  if (sourceColumnEntityId === targetColumnEntityId) return false;

  const draggedEvent = findEventById(data, eventId);

  if (!draggedEvent?.appointmentTypeId) return false;

  const hasMapping = providerAppointmentTypeMappings.some(
    (mapping) =>
      mapping.providerId === targetColumnEntityId && mapping.appointmentTypeId === draggedEvent.appointmentTypeId
  );

  return hasMapping;
};

type MessageType =
  | 'move-appointment'
  | 'move-appointment-across-entities'
  | 'move-appointment-type-mapping-warning'
  | 'overlapping-appointment-warning'
  | 'overlapping-appointment-warning-across-entities'
  | 'appointment-type-mapping-and-overlap-warning'
  | 'unassigned-drop-error'
  | null;

type Props = {
  show: boolean;
  dragDropState: DragDropState;
  onConfirm: () => void;
  onClose: () => void;
};

export const AppointmentDragDropConfirmationModal = ({ show, dragDropState, onConfirm, onClose }: Props) => {
  const { t } = useTranslation('scheduleCalendarViewMain');
  const { data, calendarDate, startHour } = useCalendarViewProps();
  const { calendarLocationIds } = useCalendarLocationIds();
  const { pendingDropPayload } = dragDropState;

  // Fetch provider and appointment type mappings for all calendar locations
  const mappingsQueries = SchedulerV3.Queries.useListMappingsQueries(
    calendarLocationIds.map((locationId) => ({
      request: {
        locationIds: [locationId],
        mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
        showInactive: false,
      },
    }))
  );

  const providerAppointmentTypeMappingsByLocation = useMemo(() => {
    const result: Record<string, { appointmentTypeId?: string; providerId?: string }[]> = {};
    mappingsQueries.forEach((query, idx) => {
      const locationId = calendarLocationIds[idx];
      result[locationId] = (query.data?.mappings ?? []).map((mapping) => ({
        appointmentTypeId: mapping.appointmentTypeMetaData?.id,
        providerId: mapping.providerMetaData?.id,
      }));
    });
    return result;
  }, [mappingsQueries, calendarLocationIds]);

  const messageType = useMemo((): MessageType => {
    if (!pendingDropPayload) return null;

    const { sourceColumnEntityId, targetColumnEntityId, targetLocationId } = pendingDropPayload;

    if (targetColumnEntityId === '') {
      return 'unassigned-drop-error';
    }

    const isAcrossEntities = sourceColumnEntityId !== targetColumnEntityId;

    // Check if target provider has mapping to the appointment's appointment type
    const providerAppointmentTypeMappings = providerAppointmentTypeMappingsByLocation[targetLocationId] ?? [];
    const hasAppointmentTypeMapping = checkAppointmentTypeMapping(
      data,
      pendingDropPayload,
      providerAppointmentTypeMappings
    );

    // Check if appointment already exist in target slot
    const hasOverlap = checkForOverlappingAppointments(data, pendingDropPayload, startHour ?? 0);

    // Handle case where both warnings apply (only possible across entities)
    if (!hasAppointmentTypeMapping && hasOverlap && isAcrossEntities) {
      return 'appointment-type-mapping-and-overlap-warning';
    }

    if (!hasAppointmentTypeMapping && isAcrossEntities) {
      return 'move-appointment-type-mapping-warning';
    }

    if (hasOverlap) {
      return isAcrossEntities ? 'overlapping-appointment-warning-across-entities' : 'overlapping-appointment-warning';
    }

    return isAcrossEntities ? 'move-appointment-across-entities' : 'move-appointment';
  }, [pendingDropPayload, data, startHour, providerAppointmentTypeMappingsByLocation]);

  const { fromDateTime, toDateTime, fromProvider, toProvider } = useMemo(() => {
    if (!pendingDropPayload) {
      return { fromDateTime: '', toDateTime: '', fromProvider: '', toProvider: '' };
    }

    const { eventId, sourceColumnEntityId, targetColumnEntityId, newStartTimeMinutes } = pendingDropPayload;

    const originalEvent = findEventById(data, eventId);
    const originalStartTime = originalEvent?.startHour ?? '';

    // TODO: Revisit this logic
    // Calculate new start time from drop position
    const newStartTime = minutesToTimeString(newStartTimeMinutes, startHour ?? 0);

    // Get entity names
    const sourceEntityName = findEntityName(data, sourceColumnEntityId);
    const targetEntityName = findEntityName(data, targetColumnEntityId);

    return {
      fromDateTime: formatDateTime(calendarDate, originalStartTime),
      toDateTime: formatDateTime(calendarDate, newStartTime),
      fromProvider: sourceEntityName,
      toProvider: targetEntityName,
    };
  }, [pendingDropPayload, data, calendarDate, startHour]);

  const message = useMemo(() => {
    switch (messageType) {
      case 'unassigned-drop-error':
        return <Text>{t('This column does not allow for drag and drop appointments.')}</Text>;
      case 'move-appointment':
        return (
          <Text>
            <Trans t={t}>
              Are you sure you want to move this appointment from <strong>{{ fromDateTime }}</strong> to{' '}
              <strong>{{ toDateTime }}</strong>?
            </Trans>
          </Text>
        );
      case 'move-appointment-across-entities':
        return (
          <Text>
            <Trans t={t}>
              Are you sure you want to move this appointment from <strong>{{ fromDateTime }}</strong>, with{' '}
              <strong>{{ fromProvider }}</strong> to <strong>{{ toDateTime }}</strong>, with{' '}
              <strong>{{ toProvider }}</strong>?
            </Trans>
          </Text>
        );
      case 'move-appointment-type-mapping-warning':
        return (
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <InlineWarningNotification type='providerMapping' />
            <Text>
              <Trans t={t}>
                Are you sure you want to move this appointment from <strong>{{ fromDateTime }}</strong>, with{' '}
                <strong>{{ fromProvider }}</strong> to <strong>{{ toDateTime }}</strong>, with{' '}
                <strong>{{ toProvider }}</strong>?
              </Trans>
            </Text>
          </div>
        );
      case 'overlapping-appointment-warning':
        return (
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <InlineWarningNotification type='overlappingAppointments' />
            <Text>
              <Trans t={t}>
                This appointment is overlapping another appointment, are you sure you want to move this appointment from{' '}
                <strong>{{ fromDateTime }}</strong> to <strong>{{ toDateTime }}</strong>?
              </Trans>
            </Text>
          </div>
        );
      case 'overlapping-appointment-warning-across-entities':
        return (
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <InlineWarningNotification type='overlappingAppointments' />
            <Text>
              <Trans t={t}>
                This appointment is overlapping another appointment, are you sure you want to move this appointment from{' '}
                <strong>{{ fromDateTime }}</strong>, with <strong>{{ fromProvider }}</strong> to{' '}
                <strong>{{ toDateTime }}</strong>, with <strong>{{ toProvider }}</strong>?
              </Trans>
            </Text>
          </div>
        );
      case 'appointment-type-mapping-and-overlap-warning':
        return (
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <InlineWarningNotification type='providerMapping' />
            <InlineWarningNotification type='overlappingAppointments' />
            <Text>
              <Trans t={t}>
                Are you sure you want to move this appointment from <strong>{{ fromDateTime }}</strong>, with{' '}
                <strong>{{ fromProvider }}</strong> to <strong>{{ toDateTime }}</strong>, with{' '}
                <strong>{{ toProvider }}</strong>?
              </Trans>
            </Text>
          </div>
        );
      default:
        return null;
    }
  }, [t, messageType, fromDateTime, toDateTime, fromProvider, toProvider]);

  if (!message) return null;

  if (messageType === 'unassigned-drop-error') {
    return <InfoModal show={show} title={t('Unable to complete move')} message={message} onClose={onClose} />;
  }

  return (
    <ConfirmationModal
      show={show}
      title={t('Move appointment?')}
      message={message}
      onClose={onClose}
      onConfirm={onConfirm}
      confirmLabel={t('Move appointment')}
      cancelLabel={t('Cancel')}
    />
  );
};

type InlineWarningNotificationType = 'providerMapping' | 'overlappingAppointments';

const InlineWarningNotification = ({ type }: { type: InlineWarningNotificationType }) => {
  const { t } = useTranslation('scheduleCalendarViewMain');

  const text = useMemo(() => {
    if (type === 'providerMapping') {
      return t('This provider does not take this kind of appointment.');
    } else if (type === 'overlappingAppointments') {
      return t('Appointments are overlapping');
    }
    return '';
  }, [t, type]);

  if (!text) return null;

  return (
    <InlineNotification size='large' status='warn' css={inlineNotificationFullWidthStyles}>
      {text}
    </InlineNotification>
  );
};

const inlineNotificationFullWidthStyles = {
  '& > div': { width: '100%' },
};

type InfoModalProps = {
  show: boolean;
  title?: string;
  message?: ReactNode | null;
  trackingId?: string;
  onClose: () => void;
};

const InfoModal = ({ show, title, message, trackingId, onClose }: InfoModalProps) => {
  const { t } = useTranslation('scheduleCalendarViewMain');

  return (
    <Modal show={show} onClose={onClose} maxWidth={400}>
      <Modal.Header onClose={onClose}>{title}</Modal.Header>
      <Modal.Body>{message}</Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Close'),
          trackingId,
          onClick: onClose,
        }}
      />
    </Modal>
  );
};
